import React, { useEffect, useRef, useState } from "react";
import { toPng } from "html-to-image";
import DatePicker, { DateObject } from "react-multi-date-picker";
import toast from "react-hot-toast";
import { Link } from "react-router-dom";
import { promotionsService } from "../../services/promotions.service";
import {
  formField,
  formInputStyle,
  formSectionCard,
  formSectionTitle,
} from "../../components/common/FormUI";

const INITIAL_PROMOTION = {
  brandName: "Nexttravelworld",
  website: "www.nexttravelworld.com",
  headline: "Unmatched & Lowest Series Fares",
  subheading: "For all domestic & international sectors",
  callToAction: "Login now",
  promotionUrl: "https://www.nexttravelworld.com",
  startDate: "",
  endDate: "",
};

// Look of each sector type's fare block in the banner
const SECTOR_STYLES = {
  middle_east: { title: "Middle East", color: "#872e83", icon: "bi-buildings-fill" },
  international: { title: "International", color: "#0866b5", icon: "bi-globe-americas" },
  domestic: { title: "Domestic", color: "#32891f", icon: "bi-airplane-fill" },
};

const SECTOR_OPTIONS = [
  { value: "all", label: "All sectors" },    
  { value: "domestic", label: "Domestic" },
  { value: "international", label: "International" },
  { value: "middle_east", label: "Middle East" },
];

const formatPrice = (amount) => `₹${Number(amount || 0).toLocaleString("en-IN")}`;

// PromotionsGroup (API) -> editable fare group used by the form and the banner previews
const toFareGroup = (group) => {
  const style = SECTOR_STYLES[group.sector_type] || {
    title: String(group.sector_type || "Fare").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
    color: "#0b367b",
    icon: "bi-airplane-fill",
  };
  return {
    ...style,
    id: `fare-group-${group.sector_type}`,
    fares: (group.sectors || []).map((fare) => ({
      route: `${fare.origin} – ${fare.destination}`,
      airline: fare.airline_code || "",
      date: fare.departure_date || "",
      price: formatPrice(fare.lowest_fare),
    })),
  };
};

const formatFareDate = (value) => {
  if (!value) return "";
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, month - 1, day)
    .toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })
    .toUpperCase();
};

const FEATURES = [
  { icon: "bi-display", label: "Live inventory" },
  { icon: "bi-lightning-charge-fill", label: "Instant PNR" },
  { icon: "bi-award", label: "Best B2B fares" },
];

const BANNER_ICON_PATHS = {
  "airplane-fill": "M21 16v-2l-8-5V3.5a1.5 1.5 0 0 0-3 0V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5z",
  "airplane": "M21 16v-2l-8-5V3.5a1.5 1.5 0 0 0-3 0V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5z",
  "buildings-fill": "M3 21V3h11v4h7v14zm2-2h2v-2H5zm0-4h2v-2H5zm0-4h2V9H5zm0-4h2V5H5zm4 12h2v-2H9zm0-4h2v-2H9zm0-4h2V9H9zm0-4h2V5H9zm7 12h2v-2h-2zm0-4h2v-2h-2zm0-4h2v-2h-2z",
  "globe-americas": "M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20m6.9 6h-3.1a15 15 0 0 0-1.3-3.5A8.1 8.1 0 0 1 18.9 8M12 4c.9 1.1 1.6 2.5 2 4h-4c.4-1.5 1.1-2.9 2-4M4 13a8 8 0 0 1 0-2h4a16 16 0 0 0 0 2zm.8 2h3.1a15 15 0 0 0 1.3 3.5A8.1 8.1 0 0 1 4.8 15M7.9 8H4.8a8.1 8.1 0 0 1 4.4-3.5A15 15 0 0 0 7.9 8M12 20c-.9-1.1-1.6-2.5-2-4h4c-.4 1.5-1.1 2.9-2 4m2.4-6H9.6a13 13 0 0 1 0-4h4.8a13 13 0 0 1 0 4m.1 4.5a15 15 0 0 0 1.3-3.5h3.1a8.1 8.1 0 0 1-4.4 3.5M16 13a16 16 0 0 0 0-2h4a8 8 0 0 1 0 2z",
  "display": "M3 4h18v13H3zm-2 15h22v2H1zM5 6v9h14V6z",
  "lightning-charge-fill": "M13 1 4 13h6l-1 10 11-14h-6z",
  "award": "M12 2a7 7 0 1 0 0 14 7 7 0 0 0 0-14m0 3a4 4 0 1 1 0 8 4 4 0 0 1 0-8m-4 10-2 8 6-3 6 3-2-8-2 2-2 1-2-1z",
  "headset": "M12 3a9 9 0 0 0-9 9v5a3 3 0 0 0 3 3h2v-8H5v-1a7 7 0 0 1 14 0v1h-3v8h3a3 3 0 0 0 3-3v-5a9 9 0 0 0-9-9m-7 11h1v4H6a1 1 0 0 1-1-1zm13 4v-4h1v3a1 1 0 0 1-1 1",
  "arrow-up-right": "M5.6 17.7 17.3 6H9.5V4h11v11h-2V7.4L7 18.9z",
  "star-fill": "m12 2 3.1 6.3 6.9 1-5 4.9 1.2 6.8L12 17.8l-6.2 3.2L7 14.2l-5-4.9 6.9-1z",
  "tag-fill": "m2 12 10-10h9v9L11 21zm14-5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3",
  "signpost-split-fill": "M3 3h8v4H7v3h10V7h4v6h-8v3h4v5h-4v-5H9v-5h8V9H7v12H3z",
};

const BannerIcon = ({ name, className, style }) => {
  const iconName = name.split(" ").pop().replace(/^bi-/, "");
  const path = BANNER_ICON_PATHS[iconName];
  if (!path) return null;

  return (
    <svg
      aria-hidden="true"
      className={className}
      viewBox="0 0 24 24"
      focusable="false"
      style={{ width: "1em", height: "1em", flexShrink: 0, verticalAlign: "-0.125em", fill: "currentColor", ...style }}
    >
      <path d={path} />
    </svg>
  );
};

const INITIAL_FARE_GROUPS = [];

const INITIAL_EXPANDED_SECTIONS = {
  promotionDetails: true,
  fetchSeries: true,
};

const buildInitialApiFilter = () => ({
  sector_type: "all",
  from_date: new DateObject().format("YYYY-MM-DD"),
  to_date: new DateObject().add(29, "days").format("YYYY-MM-DD"),
  max_limit: "1",
});

const readImageAsDataUrl = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => {
    if (typeof reader.result === "string") {
      resolve(reader.result);
    } else {
      reject(new Error("The selected image could not be read."));
    }
  };
  reader.onerror = () => reject(reader.error || new Error("The selected image could not be read."));
  reader.readAsDataURL(file);
});

const Promotions = () => {
  const [promotion, setPromotion] = useState(INITIAL_PROMOTION);
  const [fareGroups, setFareGroups] = useState(INITIAL_FARE_GROUPS);
  const [apiFilter, setApiFilter] = useState(buildInitialApiFilter);
  const [fetching, setFetching] = useState(false);
  const [fetched, setFetched] = useState(false); // Reset next to Fetch Series shows only after a fetch
  const [expandedSections, setExpandedSections] = useState(INITIAL_EXPANDED_SECTIONS);
  const [todayDate] = useState(() => new DateObject());
  const [logoImage, setLogoImage] = useState("");
  const [logoFileName, setLogoFileName] = useState("");
  const [bannerImage, setBannerImage] = useState("");
  const [bannerFileName, setBannerFileName] = useState("");
  const [backgroundImage, setBackgroundImage] = useState("");
  const [backgroundFileName, setBackgroundFileName] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [selectedPreview, setSelectedPreview] = useState(1);
  const bannerPreviewOneRef = useRef(null);
  const bannerPreviewTwoRef = useRef(null);
  const bannerPreviewThreeRef = useRef(null);
  const bannerPreviewFourRef = useRef(null);
  const activePreviewRef = selectedPreview === 1
    ? bannerPreviewOneRef
    : selectedPreview === 2
      ? bannerPreviewTwoRef
      : selectedPreview === 3
        ? bannerPreviewThreeRef
        : bannerPreviewFourRef;

  useEffect(() => () => {
    if (bannerImage) URL.revokeObjectURL(bannerImage);
  }, [bannerImage]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setPromotion((current) => ({ ...current, [name]: value }));
  };

  const handleApiFilterChange = (event) => {
    const { name, value } = event.target;
    setApiFilter((current) => ({ ...current, [name]: value }));
  };

  // Fetch Series by Api: sector-fares for the chosen sector type and departure window
  const handleFetchSeries = async () => {
    const { sector_type, from_date, to_date, max_limit: limitInput } = apiFilter;
    if (!from_date || !to_date) return toast.error("Select both from and to dates.");
    if (to_date < from_date) return toast.error("To date cannot be before from date.");
    const n = Number(limitInput);
    if (!Number.isInteger(n) || n < 1 || n > 500) return toast.error("Enter a max limit between 1 and 500.");
    const max_limit = n;

    setFetching(true);
    setFetched(true);
    try {
      const res = await promotionsService.list({ sector_type, from_date, to_date, max_limit });
      if (!res?.success) return toast.error(res?.message || "Could not fetch series fares.");
      const groups = (Array.isArray(res.resData) ? res.resData : []).map(toFareGroup);
      setFareGroups(groups);
      setExpandedSections((sections) => ({
        promotionDetails: sections.promotionDetails,
        fetchSeries: sections.fetchSeries,
      }));
      if (groups.every((g) => g.fares.length === 0)) toast("No series fares found for these dates.");
      else toast.success("Series fares loaded.");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not fetch series fares.");
    } finally {
      setFetching(false);
    }
  };

  // Clear the fetched fares and put the fetch fields back to their defaults
  const handleResetFetch = () => {
    setApiFilter(buildInitialApiFilter());
    setFareGroups(INITIAL_FARE_GROUPS);
    setFetched(false);
  };

  const toggleSection = (sectionId) => {
    setExpandedSections((sections) => ({
      ...sections,
      [sectionId]: !sections[sectionId],
    }));
  };

  const handleFareChange = (groupId, fareIndex, field, value) => {
    setFareGroups((groups) =>
      groups.map((group) => group.id === groupId
        ? {
          ...group,
          fares: group.fares.map((fare, index) =>
            index === fareIndex ? { ...fare, [field]: value } : fare,
          ),
        }
        : group,
      ),
    );
  };

  const handleAddFare = (groupId) => {
    setFareGroups((groups) =>
      groups.map((group) => group.id === groupId
        ? {
          ...group,
          fares: [...group.fares, {
            route: "",
            airline: "",
            date: todayDate.format("YYYY-MM-DD"),
            price: "",
          }],
        }
        : group,
      ),
    );
  };

  const handleRemoveFare = (groupId, fareIndex) => {
    setFareGroups((groups) =>
      groups.map((group) => group.id === groupId
        ? { ...group, fares: group.fares.filter((_, index) => index !== fareIndex) }
        : group,
      ),
    );
  };

  const handleImageChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Choose an image file for the promotion banner.");
      event.target.value = "";
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("The banner image must be 10 MB or smaller.");
      event.target.value = "";
      return;
    }

    setBannerImage(URL.createObjectURL(file));
    setBannerFileName(file.name);
  };

  const handleLogoImageChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Choose an image file for the company logo.");
      event.target.value = "";
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("The company logo must be 10 MB or smaller.");
      event.target.value = "";
      return;
    }

    try {
      const dataUrl = await readImageAsDataUrl(file);
      setLogoImage(dataUrl);
      setLogoFileName(file.name);
    } catch (error) {
      const message = error instanceof Error ? error.message : "The selected image could not be read.";
      toast.error(message);
      event.target.value = "";
    }
  };

  const handleBackgroundImageChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Choose an image file for the background.");
      event.target.value = "";
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("The background image must be 10 MB or smaller.");
      event.target.value = "";
      return;
    }

    try {
      const dataUrl = await readImageAsDataUrl(file);
      setBackgroundImage(dataUrl);
      setBackgroundFileName(file.name);
    } catch (error) {
      const message = error instanceof Error ? error.message : "The selected image could not be read.";
      toast.error(message);
      event.target.value = "";
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    toast.success("Banner preview updated. Saving and publishing need a promotions API.");
  };

  const handleDownload = async () => {
    if (!activePreviewRef.current) {
      toast.error("The banner preview is not ready to download.");
      return;
    }

    setDownloading(true);
    try {
      const dataUrl = await toPng(activePreviewRef.current, {
        cacheBust: true,
        pixelRatio: 2,
      });
      const downloadLink = document.createElement("a");
      downloadLink.download = `promotion-banner-${selectedPreview}.png`;
      downloadLink.href = dataUrl;
      downloadLink.click();
      toast.success("Promotion banner downloaded.");
    } catch (error) {
      const message = error instanceof Error
        ? error.message
        : error && typeof error === "object" && "target" in error
          ? "An image could not be loaded for export."
          : "The banner image could not be rendered.";
      toast.error(`Could not download the banner: ${message}`);
    } finally {
      setDownloading(false);
    }
  };

  const handleReset = () => {
    setPromotion(INITIAL_PROMOTION);
    setFareGroups(INITIAL_FARE_GROUPS);
    setApiFilter(buildInitialApiFilter());
    setFetched(false);
    setExpandedSections(INITIAL_EXPANDED_SECTIONS);
    setLogoImage("");
    setLogoFileName("");
    setBannerImage("");
    setBannerFileName("");
    setBackgroundImage("");
    setBackgroundFileName("");
    ["promotion-logo-image", "promotion-banner-image", "promotion-background-image"].forEach((id) => {
      const fileInput = document.getElementById(id);
      if (fileInput) fileInput.value = "";
    });
  };

  const inputStyle = { ...formInputStyle, width: "100%" };
  const previewUrl = promotion.website.replace(/^https?:\/\//i, "");
  const today = todayDate.format("YYYY-MM-DD");
  const visibleFareGroups = fareGroups
    .map((group) => ({
      ...group,
      fares: group.fares.filter((fare) =>
        Object.values(fare).some((value) => value.trim()),
      ),
    }))
    .filter((group) => group.fares.length > 0);

  return (
    <div className="container-fluid py-3">
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
        <div>
          <h4 className="mb-1" style={{ fontSize: 20, fontWeight: 700, color: "#1e293b" }}>
            Promotions
          </h4>
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb mb-0" style={{ fontSize: 12, color: "#8a9ab5" }}>
              <li className="breadcrumb-item">
                <Link to="/dashboard"><i className="bi bi-house-door-fill me-1" />Home</Link>
              </li>
              <li className="breadcrumb-item active" aria-current="page">Create Banner</li>
            </ol>
          </nav>
        </div>
        <span
          className="badge rounded-pill"
          style={{ padding: "8px 12px", color: "#475569", background: "#eef2ff", fontSize: 12 }}
        >
          <i className="bi bi-eye me-1" />Live preview
        </span>
      </div>

      <div className="row g-4 align-items-start">
        <div className="col-12 col-xl-5">
          <form className="v2a-form" onSubmit={handleSubmit}>
            <div style={formSectionCard}>
              <div style={{ position: "relative" }}>
                {formSectionTitle("bi bi-megaphone-fill", "Promotion details", "Set the campaign identity and offer copy.")}
                <button
                  type="button"
                  className="btn btn-sm"
                  aria-expanded={!!expandedSections.promotionDetails}
                  onClick={() => toggleSection("promotionDetails")}
                  style={{ position: "absolute", top: -8, right: 22, zIndex: 1, height: 32, border: "1px solid #dbe2ea", borderRadius: 8, background: "#fff", color: "#475569", fontSize: 11 }}
                >
                  <i className={`bi bi-chevron-${expandedSections.promotionDetails ? "up" : "down"} me-1`} />
                  {expandedSections.promotionDetails ? "Collapse" : "Expand"}
                </button>
              </div>
              {expandedSections.promotionDetails && <div className="row g-3">
                {formField("Brand name", true, (
                  <input className="form-control" name="brandName" value={promotion.brandName} onChange={handleChange} required maxLength={50} style={inputStyle} />
                ))}
                {formField("Company logo", false, (
                  <div>
                    <label
                      htmlFor="promotion-logo-image"
                      className="d-flex align-items-center gap-2"
                      style={{
                        minHeight: 42, border: "1.5px dashed #cbd5e1", borderRadius: 10,
                        background: "#f8fafc", padding: "8px 12px", cursor: "pointer",
                        color: "#475569", fontSize: 12,
                      }}
                    >
                      <i className="bi bi-image" style={{ fontSize: 17 }} />
                      <span className="text-truncate">{logoFileName || "Choose company logo"}</span>
                    </label>
                    <input
                      id="promotion-logo-image"
                      className="visually-hidden"
                      type="file"
                      accept="image/*"
                      onChange={handleLogoImageChange}
                    />
                    {logoImage && (
                      <img
                        src={logoImage}
                        alt="Company logo"
                        className="mt-2"
                        style={{ maxWidth: 180, maxHeight: 56, objectFit: "contain", border: "1px solid #e2e8f0", borderRadius: 6, background: "#fff", padding: 4 }}
                      />
                    )}
                    <small className="d-block mt-1" style={{ color: "#94a3b8", fontSize: 11 }}>
                      Shown in all banner previews · up to 10 MB
                    </small>
                  </div>
                ))}
                {formField("Website", true, (
                  <input className="form-control" name="website" value={promotion.website} onChange={handleChange} required maxLength={100} style={inputStyle} />
                ))}
                {formField("Main offer headline", true, (
                  <input className="form-control" name="headline" value={promotion.headline} onChange={handleChange} required maxLength={70} style={inputStyle} />
                ), "col-12")}
                {formField("Supporting message", false, (
                  <input className="form-control" name="subheading" value={promotion.subheading} onChange={handleChange} maxLength={100} style={inputStyle} />
                ), "col-12")}
                {formField("Button label", true, (
                  <input className="form-control" name="callToAction" value={promotion.callToAction} onChange={handleChange} required maxLength={30} style={inputStyle} />
                ))}
                {formField("Button destination URL", true, (
                  <input className="form-control" type="url" name="promotionUrl" value={promotion.promotionUrl} onChange={handleChange} required maxLength={250} style={inputStyle} />
                ), "col-12")}
                {formField("Background image", false, (
                  <div>
                    <label
                      htmlFor="promotion-background-image"
                      className="d-flex align-items-center gap-2"
                      style={{
                        minHeight: 42, border: "1.5px dashed #cbd5e1", borderRadius: 10,
                        background: "#f8fafc", padding: "8px 12px", cursor: "pointer",
                        color: "#475569", fontSize: 12,
                      }}
                    >
                      <i className="bi bi-image" style={{ fontSize: 17 }} />
                      <span className="text-truncate">{backgroundFileName || "Choose background image"}</span>
                    </label>
                    <input
                      id="promotion-background-image"
                      className="visually-hidden"
                      type="file"
                      accept="image/*"
                      onChange={handleBackgroundImageChange}
                    />
                    <small className="d-block mt-1" style={{ color: "#94a3b8", fontSize: 11 }}>
                      Used as the background in the banner preview · up to 10 MB
                    </small>
                  </div>
                ), "col-12")}
              </div>}
            </div>

            <div style={formSectionCard}>
              <div style={{ position: "relative" }}>
                {formSectionTitle("bi bi-cloud-download-fill", "Fetch Series Fares", "Load the lowest series fares for a sector type and departure window.")}
                <button
                  type="button"
                  className="btn btn-sm"
                  aria-expanded={!!expandedSections.fetchSeries}
                  onClick={() => toggleSection("fetchSeries")}
                  style={{ position: "absolute", top: -8, right: 22, zIndex: 1, height: 32, border: "1px solid #dbe2ea", borderRadius: 8, background: "#fff", color: "#475569", fontSize: 11 }}
                >
                  <i className={`bi bi-chevron-${expandedSections.fetchSeries ? "up" : "down"} me-1`} />
                  {expandedSections.fetchSeries ? "Collapse" : "Expand"}
                </button>
              </div>
              {expandedSections.fetchSeries && <div className="row g-3">
                {formField("Sector type", true, (
                  <select className="form-select" name="sector_type" value={apiFilter.sector_type} onChange={handleApiFilterChange} style={inputStyle}>
                    {SECTOR_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                ))}
                {formField("Max limit (1-500)", true, (
                  <input className="form-control" type="number" min={1} max={500} step={1} name="max_limit" value={apiFilter.max_limit} onChange={handleApiFilterChange} placeholder="Sectors per type" style={inputStyle} />
                ))}
                {formField("From date", true, (
                  <DatePicker
                    id="fetch-from-date"
                    value={apiFilter.from_date ? new DateObject({ date: apiFilter.from_date, format: "YYYY-MM-DD" }) : null}
                    onChange={(date) => setApiFilter((current) => {
                      const from_date = date ? date.format("YYYY-MM-DD") : "";
                      return { ...current, from_date, to_date: from_date && current.to_date < from_date ? from_date : current.to_date };
                    })}
                    format="DD MMM YYYY"
                    currentDate={todayDate}
                    editable={false}
                    placeholder="From date"
                    inputClass="form-control"
                    containerStyle={{ width: "100%" }}
                    style={{ ...inputStyle, minWidth: 0 }}
                  />
                ), "col-12 col-sm-4")}
                {formField("To date", true, (
                  <DatePicker
                    id="fetch-to-date"
                    value={apiFilter.to_date ? new DateObject({ date: apiFilter.to_date, format: "YYYY-MM-DD" }) : null}
                    onChange={(date) => setApiFilter((current) => ({ ...current, to_date: date ? date.format("YYYY-MM-DD") : "" }))}
                    format="DD MMM YYYY"
                    currentDate={todayDate}
                    minDate={apiFilter.from_date ? new DateObject({ date: apiFilter.from_date, format: "YYYY-MM-DD" }) : undefined}
                    editable={false}
                    placeholder="To date"
                    inputClass="form-control"
                    containerStyle={{ width: "100%" }}
                    style={{ ...inputStyle, minWidth: 0 }}
                  />
                ), "col-12 col-sm-4")}
                <div className="col-12 col-sm-4 d-flex align-items-end gap-2">
                  <button
                    type="button"
                    className="btn btn-sm px-3 flex-grow-1"
                    onClick={handleFetchSeries}
                    disabled={fetching}
                    style={{ height: formInputStyle.height || 42, borderRadius: 8, border: 0, background: "var(--btn-bg)", color: "#fff", fontWeight: 600, fontSize: 13, whiteSpace: "nowrap" }}
                  >
                    <i className={`bi ${fetching ? "bi-hourglass-split" : "bi-cloud-download"} me-1`} />
                    {fetching ? "Fetching..." : "Fetch Series"}
                  </button>
                  {fetched && (
                    <button
                      type="button"
                      className="btn btn-sm px-3"
                      onClick={handleResetFetch}
                      disabled={fetching}
                      style={{ height: formInputStyle.height || 42, borderRadius: 8, border: "1px solid #dbe2ea", background: "#fff", color: "#475569", fontSize: 13 }}
                    >
                      Reset
                    </button>
                  )}
                </div>
              </div>}
            </div>

            {fareGroups.map((group) => (
              <div key={group.id} style={formSectionCard}>
                <div style={{ position: "relative" }}>
                  {formSectionTitle("bi bi-ticket-perforated-fill", `${group.title || "Fare"} series fares`, "Edit, add or remove fare rows for this section.", group.color)}
                  <button
                    type="button"
                    className="btn btn-sm"
                    aria-expanded={!!expandedSections[group.id]}
                    onClick={() => toggleSection(group.id)}
                    style={{ position: "absolute", top: -8, right: 22, zIndex: 1, height: 32, border: "1px solid #dbe2ea", borderRadius: 8, background: "#fff", color: "#475569", fontSize: 11 }}
                  >
                    <i className={`bi bi-chevron-${expandedSections[group.id] ? "up" : "down"} me-1`} />
                    {expandedSections[group.id] ? "Collapse" : "Expand"}
                  </button>
                </div>
                {expandedSections[group.id] && <>
                <div className="d-flex justify-content-between align-items-center gap-2 mb-2">
                  <span style={{ color: "#64748b", fontSize: 12 }}>
                    {group.fares.length} fare {group.fares.length === 1 ? "row" : "rows"}
                  </span>
                  <button
                    type="button"
                    className="btn btn-sm px-3"
                    onClick={() => handleAddFare(group.id)}
                    style={{ height: 32, borderRadius: 8, border: "1px solid #dbe2ea", background: "#fff", color: "#334155", fontSize: 12 }}
                  >
                    <i className="bi bi-plus-lg me-1" />Add fare
                  </button>
                </div>
                <div className="d-flex flex-column gap-2">
                  {group.fares.map((fare, fareIndex) => (
                    <div
                      key={`${group.id}-fare-${fareIndex}`}
                      className="row g-2 align-items-center"
                      style={{ padding: "10px 8px", borderRadius: 10, background: "#f8fafc", border: "1px solid #eef1f7" }}
                    >
                      <div className="col-12 col-md-4">
                        <label htmlFor={`${group.id}-route-${fareIndex}`} className="visually-hidden">Route</label>
                        <input
                          id={`${group.id}-route-${fareIndex}`}
                          className="form-control"
                          value={fare.route}
                          onChange={(event) => handleFareChange(group.id, fareIndex, "route", event.target.value)}
                          placeholder="Route (e.g. DEL – DXB)"
                          maxLength={30}
                          style={{ ...inputStyle, height: 36, fontSize: 12 }}
                        />
                      </div>
                      <div className="col-4 col-md-2">
                        <label htmlFor={`${group.id}-airline-${fareIndex}`} className="visually-hidden">Airline</label>
                        <input
                          id={`${group.id}-airline-${fareIndex}`}
                          className="form-control"
                          value={fare.airline}
                          onChange={(event) => handleFareChange(group.id, fareIndex, "airline", event.target.value)}
                          placeholder="Airline"
                          maxLength={8}
                          style={{ ...inputStyle, height: 36, fontSize: 12 }}
                        />
                      </div>
                      <div className="col-8 col-md-3">
                        <label htmlFor={`${group.id}-date-${fareIndex}`} className="visually-hidden">Departure date</label>
                        <DatePicker
                          id={`${group.id}-date-${fareIndex}`}
                          value={fare.date
                            ? new DateObject({ date: fare.date, format: "YYYY-MM-DD" })
                            : null}
                          onChange={(date) => handleFareChange(
                            group.id,
                            fareIndex,
                            "date",
                            date ? date.format("YYYY-MM-DD") : "",
                          )}
                          format="DD MMM YYYY"
                          currentDate={todayDate}
                          minDate={todayDate}
                          editable={false}
                          placeholder="Departure date"
                          inputClass="form-control"
                          containerStyle={{ width: "100%" }}
                          style={{ ...inputStyle, height: 36, minWidth: 0, fontSize: 12, paddingLeft: 8, paddingRight: 8 }}
                        />
                      </div>
                      <div className="col-10 col-md-2">
                        <label htmlFor={`${group.id}-price-${fareIndex}`} className="visually-hidden">Fare</label>
                        <input
                          id={`${group.id}-price-${fareIndex}`}
                          className="form-control"
                          value={fare.price}
                          onChange={(event) => handleFareChange(group.id, fareIndex, "price", event.target.value)}
                          placeholder="Fare"
                          maxLength={16}
                          style={{ ...inputStyle, height: 36, minWidth: 0, fontSize: 12 }}
                        />
                      </div>
                      <div className="col-2 col-md-1 d-flex justify-content-end">
                        <button
                          type="button"
                          className="btn btn-sm flex-shrink-0"
                          aria-label={`Remove fare row ${fareIndex + 1} from ${group.title || "fare section"}`}
                          onClick={() => handleRemoveFare(group.id, fareIndex)}
                          style={{ width: 34, height: 36, padding: 0, border: 0, borderRadius: 8, background: "#fff1f2", color: "#dc2626" }}
                        >
                          <i className="bi bi-trash" />
                        </button>
                      </div>
                    </div>
                  ))}
                  {group.fares.length === 0 && (
                    <div className="text-center py-3" style={{ color: "#94a3b8", fontSize: 12 }}>
                      No fares yet. Use “Add fare” to add a row.
                    </div>
                  )}
                </div>
                </>}
              </div>
            ))}

            <div style={{ ...formSectionCard, display: "none" }}>
              {formSectionTitle("bi bi-image", "Banner artwork", "Attach a finished banner or check the selected background.")}
              {backgroundImage && (
                <div className="d-flex align-items-center gap-3 mb-3 p-2" style={{ border: "1px solid #e2e8f0", borderRadius: 10, background: "#f8fafc" }}>
                  <img src={backgroundImage} alt="Selected promotion background" style={{ width: 72, height: 48, objectFit: "cover", borderRadius: 6 }} />
                  <div className="min-w-0">
                    <div style={{ fontSize: 12, fontWeight: 600, color: "#334155" }}>Background attached</div>
                    <div className="text-truncate" style={{ fontSize: 11, color: "#64748b" }}>{backgroundFileName}</div>
                  </div>
                </div>
              )}
              <label
                htmlFor="promotion-banner-image"
                className="d-flex flex-column align-items-center justify-content-center text-center"
                style={{
                  minHeight: 124, border: "1.5px dashed #cbd5e1", borderRadius: 12,
                  background: "#f8fafc", padding: 18, cursor: "pointer",
                }}
              >
                <i className="bi bi-cloud-arrow-up" style={{ fontSize: 28, color: "#64748b" }} />
                <span className="mt-1" style={{ color: "#334155", fontWeight: 600, fontSize: 13 }}>
                  {bannerFileName || "Choose a banner image"}
                </span>
                <span className="mt-1" style={{ color: "#94a3b8", fontSize: 12 }}>
                  PNG, JPG or WEBP · up to 10 MB
                </span>
              </label>
              <input
                id="promotion-banner-image"
                className="visually-hidden"
                type="file"
                accept="image/*"
                onChange={handleImageChange}
              />
            </div>

            <div style={{ ...formSectionCard, display: "none" }}>
              {formSectionTitle("bi bi-calendar-event", "Campaign schedule", "Choose when this promotion should run.")}
              <div className="row g-3">
                {formField("Start date", false, (
                  <input className="form-control" type="date" name="startDate" value={promotion.startDate} onChange={handleChange} style={inputStyle} />
                ))}
                {formField("End date", false, (
                  <input
                    className="form-control"
                    type="date"
                    name="endDate"
                    value={promotion.endDate}
                    onChange={handleChange}
                    min={promotion.startDate || undefined}
                    style={inputStyle}
                  />
                ))}
              </div>
            </div>

            <div className="d-flex justify-content-end gap-2 mb-4">
              <button type="button" className="btn btn-sm px-3" onClick={handleReset} style={{ height: 40, borderRadius: 8, border: "1px solid #dbe2ea", background: "#fff", color: "#475569", fontSize: 13 }}>
                Reset
              </button>
              {/* <button type="submit" className="btn btn-sm px-4" style={{ height: 40, borderRadius: 8, border: 0, background: "linear-gradient(135deg, var(--btn-bg) 0%, var(--btn-hover-bg) 100%)", color: "#fff", fontWeight: 600, fontSize: 13 }}>
                <i className="bi bi-arrow-repeat me-1" />Update Preview
              </button> */}
            </div>
          </form>
        </div>

        <div className="col-12 col-xl-7">
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
            <div>
              <h5 className="mb-0" style={{ color: "#1e293b", fontSize: 15, fontWeight: 700 }}>Banner preview</h5>
              <span style={{ color: "#94a3b8", fontSize: 11 }}>Preview only · not published</span>
            </div>
            <div className="d-flex align-items-center gap-2">
              <button
                type="button"
                className="btn btn-sm px-3"
                aria-pressed={selectedPreview === 1}
                onClick={() => setSelectedPreview(1)}
                style={{ height: 34, borderRadius: 8, border: selectedPreview === 1 ? 0 : "1px solid #dbe2ea", background: selectedPreview === 1 ? "#4f3df5" : "#fff", color: selectedPreview === 1 ? "#fff" : "#475569", fontSize: 13 }}
              >
                Preview 1
              </button>
              <button
                type="button"
                className="btn btn-sm px-3"
                aria-pressed={selectedPreview === 2}
                onClick={() => setSelectedPreview(2)}
                style={{ height: 34, borderRadius: 8, border: selectedPreview === 2 ? 0 : "1px solid #dbe2ea", background: selectedPreview === 2 ? "#4f3df5" : "#fff", color: selectedPreview === 2 ? "#fff" : "#475569", fontSize: 13 }}
              >
                Preview 2
              </button>
              <button
                type="button"
                className="btn btn-sm px-3"
                aria-pressed={selectedPreview === 3}
                onClick={() => setSelectedPreview(3)}
                style={{ height: 34, borderRadius: 8, border: selectedPreview === 3 ? 0 : "1px solid #dbe2ea", background: selectedPreview === 3 ? "#4f3df5" : "#fff", color: selectedPreview === 3 ? "#fff" : "#475569", fontSize: 13 }}
              >
               Preview 3
              </button>
              <button
                type="button"
                className="btn btn-sm px-3"
                aria-pressed={selectedPreview === 4}
                onClick={() => setSelectedPreview(4)}
                style={{ height: 34, borderRadius: 8, border: selectedPreview === 4 ? 0 : "1px solid #dbe2ea", background: selectedPreview === 4 ? "#4f3df5" : "#fff", color: selectedPreview === 4 ? "#fff" : "#475569", fontSize: 13 }}
              >
                Preview 4
              </button>
              <button
                type="button"
                className="btn btn-sm px-3"
                onClick={handleDownload}
                disabled={downloading}
                style={{ height: 34, borderRadius: 8, border: 0, background: "#0b367b", color: "#fff", fontSize: 13 }}
              >
                <i className={`bi ${downloading ? "bi-hourglass-split" : "bi-download"} me-1`} />
                {downloading ? "Preparing..." : "Download"}
              </button>
            </div>
          </div>
          {bannerImage ? (
            <div ref={activePreviewRef} className="card border-0 shadow-sm" style={{ borderRadius: 16, overflow: "hidden" }}>
              <img src={bannerImage} alt="Uploaded promotion banner preview" style={{ display: "block", width: "100%", height: "auto" }} />
            </div>
          ) : selectedPreview === 1 ? (
            <div
              ref={activePreviewRef}
              className="card border-0 shadow-sm"
              style={{ borderRadius: 16, overflow: "hidden", background: "#fff" }}
            >
              <div
                style={{
                  padding: "clamp(16px, 3vw, 28px)",
                  background: backgroundImage
                    ? "#fff"
                    : "linear-gradient(110deg, #f7fbff 0%, #fff 57%, #e5f4ff 100%)",
                  borderBottom: "5px solid #0b367b",
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                {backgroundImage && (
                  <>
                    <img
                      src={backgroundImage}
                      alt=""
                      aria-hidden="true"
                      style={{
                        position: "absolute",
                        inset: 0,
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        pointerEvents: "none",
                      }}
                    />
                    <div
                      aria-hidden="true"
                      style={{
                        position: "absolute",
                        inset: 0,
                        background: "rgba(255, 255, 255, .78)",
                        pointerEvents: "none",
                      }}
                    />
                  </>
                )}
                <div className="d-flex align-items-center justify-content-center gap-2 mb-3" style={{ position: "relative", zIndex: 1 }}>
                  {logoImage ? (
                    <img src={logoImage} alt={`${promotion.brandName || "Company"} logo`} style={{ display: "block", maxWidth: "min(70%, 240px)", maxHeight: 58, objectFit: "contain", borderRadius: 5, background: "#fff", padding: 4 }} />
                  ) : (
                    <>
                      <span className="d-inline-flex align-items-center justify-content-center" style={{ width: 38, height: 38, borderRadius: "50%", background: "#0b4c9d", color: "#fff", fontSize: 20 }}>
                        <BannerIcon name="bi-airplane-fill" />
                      </span>
                      <div>
                        <div style={{ color: "#101828", fontSize: "clamp(16px, 2.7vw, 25px)", lineHeight: 1, fontWeight: 800 }}>
                          {promotion.brandName || "Your Travel Brand"}
                        </div>
                        <div style={{ color: "#64748b", fontSize: 9, letterSpacing: 1 }}>TRAVEL AND TOUR</div>
                      </div>
                    </>
                  )}
                </div>

                <div className="text-center" style={{ position: "relative", zIndex: 1 }}>
                  <div style={{ color: "#0b367b", fontWeight: 900, fontSize: 12, letterSpacing: 2 }}>ONLY</div>
                  <div style={{ color: "#0b367b", fontSize: "clamp(16px, 3.5vw, 29px)", fontWeight: 900, lineHeight: 1.1, overflowWrap: "anywhere" }}>
                    {previewUrl || "YOURWEBSITE.COM"}
                  </div>
                  <div className="d-flex align-items-center justify-content-center gap-2 my-2" style={{ color: "#0b367b" }}>
                    <span style={{ height: 1, background: "#0b367b", width: "20%" }} />
                    <BannerIcon name="bi-star-fill" style={{ fontSize: 11 }} />
                    <span style={{ height: 1, background: "#0b367b", width: "20%" }} />
                  </div>
                  <div style={{ display: "inline-block", padding: "4px 10px", borderRadius: 7, background: "#0b367b", color: "#fff", fontSize: "clamp(13px, 2.6vw, 21px)", fontWeight: 900, textTransform: "uppercase" }}>
                    <span style={{ color: "#fbd319" }}>{promotion.headline || "Your promotional offer"}</span>
                  </div>
                  <div className="mt-1" style={{ color: "#272727", fontSize: "clamp(11px, 2vw, 16px)", fontWeight: 800, textTransform: "uppercase" }}>
                    {promotion.subheading || "Add a supporting message"}
                  </div>
                </div>

                <div className="d-flex justify-content-center flex-wrap gap-3 gap-md-4 mt-3" style={{ position: "relative", zIndex: 1 }}>
                  {FEATURES.map((feature) => (
                    <div key={feature.label} className="d-flex align-items-center gap-2" style={{ color: "#1e293b", fontSize: "clamp(9px, 1.5vw, 12px)", fontWeight: 800, textTransform: "uppercase" }}>
                      <span className="d-inline-flex align-items-center justify-content-center" style={{ width: 28, height: 28, background: "#0b4c9d", borderRadius: "50%", color: "#fff", fontSize: 14 }}>
                        <BannerIcon name={feature.icon} />
                      </span>
                      {feature.label}
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ padding: "7px 6%", background: "#092e6b" }}>
                <div className="d-flex align-items-center justify-content-center gap-2" style={{ border: "1px dashed rgba(255,255,255,.8)", borderRadius: 7, padding: "6px 10px", color: "#fff" }}>
                  <span style={{ fontWeight: 900, fontSize: "clamp(13px, 2.4vw, 19px)", textTransform: "uppercase" }}>{promotion.callToAction || "Explore now"}</span>
                  <a
                    href={promotion.promotionUrl}
                    target="_blank"
                    rel="noreferrer"
                    style={{ borderRadius: 7, background: "#fbd319", color: "#092e6b", padding: "4px 9px", fontWeight: 900, fontSize: "clamp(11px, 2vw, 16px)", overflowWrap: "anywhere" }}
                  >
                    {previewUrl || "YOURWEBSITE.COM"} <BannerIcon name="bi-arrow-up-right" className="ms-1" />
                  </a>
                </div>
              </div>

              <div className="row g-2 p-3 p-md-4">
                {visibleFareGroups.map((group) => (
                  <div className="col-12 col-md-4" key={group.id}>
                    <div style={{ height: "100%", overflow: "hidden", borderRadius: 11, border: `1px solid ${group.color}55`, display: "flex", flexDirection: "column" }}>
                      <div className="d-flex align-items-center justify-content-center gap-2 text-white text-uppercase" style={{ minHeight: 43, background: group.color, fontSize: "clamp(11px, 1.7vw, 14px)", fontWeight: 900, textAlign: "center", padding: "6px 4px" }}>
                        <BannerIcon name={group.icon} style={{ fontSize: 18 }} />{group.title || "Fare"} series fares
                      </div>
                      {group.fares.map((fare, fareIndex) => (
                        <div key={`${group.id}-preview-${fareIndex}`} className="d-flex justify-content-between align-items-center" style={{ minHeight: 31, padding: "4px 6px", borderBottom: "1px solid #e5e7eb", color: "#222", fontSize: "clamp(8px, 1.15vw, 10px)", fontWeight: 600, whiteSpace: "nowrap" }}>
                          <span>{fare.route}</span><span>{fare.airline}</span><span>{formatFareDate(fare.date)}</span><strong>{fare.price}</strong>
                        </div>
                      ))}
                      <div className="d-flex align-items-center gap-1 mt-auto text-white" style={{ minHeight: 34, background: group.color, padding: "5px 7px", fontSize: 9 }}>
                        <BannerIcon name="bi-tag-fill" />Fares and dates for other sectors available
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mx-3 mx-md-4 mb-3 d-flex justify-content-around align-items-center flex-wrap gap-2" style={{ padding: "10px 6px", border: "1px solid #cbd5e1", borderRadius: 10, color: "#0b367b" }}>
                {FEATURES.map((feature) => (
                  <div key={`bottom-${feature.label}`} className="d-flex align-items-center gap-2" style={{ fontSize: "clamp(9px, 1.4vw, 12px)", fontWeight: 900, textTransform: "uppercase" }}>
                    <span className="d-inline-flex align-items-center justify-content-center" style={{ width: 35, height: 35, borderRadius: "50%", background: "#0b4c9d", color: "#fff", fontSize: 17 }}>
                      <BannerIcon name={feature.icon} />
                    </span>
                    {feature.label}
                  </div>
                ))}
              </div>

              <div className="d-flex align-items-center justify-content-between gap-2 flex-wrap" style={{ padding: "10px 4%", background: "#092e6b", color: "#fff" }}>
                <div>
                  <div style={{ fontSize: "clamp(13px, 2.3vw, 20px)", fontWeight: 900, overflowWrap: "anywhere" }}>{previewUrl || "YOURWEBSITE.COM"}</div>
                  <div style={{ color: "#fbd319", fontSize: "clamp(8px, 1.4vw, 11px)", fontWeight: 700, textTransform: "uppercase" }}>Your trusted partner for lowest airfares worldwide</div>
                </div>
                <div className="d-flex align-items-center gap-2" style={{ border: "1px dashed rgba(255,255,255,.8)", borderRadius: 7, padding: "5px 8px", fontSize: 10, fontWeight: 700 }}>
                  <BannerIcon name="bi-headset" style={{ fontSize: 20 }} />24×7 support<br />for our agents
                </div>
              </div>
            </div>
          ) : selectedPreview === 2 ? (
            <div
              ref={activePreviewRef}
              className="card border-0 shadow-sm"
              style={{ borderRadius: 16, overflow: "hidden", background: "#f8fafc" }}
            >
              <div style={{ position: "relative", overflow: "hidden", background: backgroundImage ? "transparent" : "#082d68", color: "#fff", padding: "clamp(14px, 2.5vw, 24px)" }}>
                {backgroundImage && (
                  <img
                    src={backgroundImage}
                    alt=""
                    aria-hidden="true"
                    style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", pointerEvents: "none" }}
                  />
                )}
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-4" style={{ position: "relative", zIndex: 1 }}>
                  <div style={{ flex: "1 1 300px" }}>
                    <div className="d-flex align-items-center gap-2 mb-2">
                      {logoImage ? (
                        <img src={logoImage} alt={`${promotion.brandName || "Company"} logo`} style={{ display: "block", maxWidth: "min(80%, 220px)", maxHeight: 46, objectFit: "contain", borderRadius: 5, background: "#fff", padding: 4 }} />
                      ) : (
                        <>
                          <span className="d-inline-flex align-items-center justify-content-center" style={{ width: 36, height: 36, borderRadius: 10, background: "#fff", color: "#0b4c9d", fontSize: 18 }}>
                            <BannerIcon name="bi-airplane-fill" />
                          </span>
                          <div>
                            <div style={{ fontSize: "clamp(16px, 2.7vw, 24px)", fontWeight: 800, lineHeight: 1.1 }}>{promotion.brandName || "Your Travel Brand"}</div>
                            <div style={{ color: "#bcd9ff", fontSize: 9, letterSpacing: 1.5 }}>TRAVEL AND TOUR</div>
                          </div>
                        </>
                      )}
                    </div>
                    <div style={{ color: "#fbd319", fontSize: 11, letterSpacing: 2, fontWeight: 800 }}>EXCLUSIVE SERIES FARE OFFERS</div>
                    <div style={{ marginTop: 4, fontSize: "clamp(19px, 3.5vw, 32px)", lineHeight: 1.05, fontWeight: 900, textTransform: "uppercase" }}>
                      {promotion.headline || "Your promotional offer"}
                    </div>
                    <div style={{ marginTop: 6, color: "#dbeafe", fontSize: "clamp(11px, 1.6vw, 14px)", fontWeight: 600 }}>
                      {promotion.subheading || "Add a supporting message"}
                    </div>
                  </div>
                  <div className="text-center" style={{ flex: "0 1 190px", padding: 14, border: "1px solid rgba(255,255,255,.35)", borderRadius: 13, background: "rgba(255,255,255,.1)", backdropFilter: "blur(4px)" }}>
                    <div style={{ fontSize: 10, letterSpacing: 1.5, color: "#dbeafe" }}>BOOK YOUR FARES</div>
                    <div style={{ margin: "6px 0 9px", color: "#fff", fontSize: 15, fontWeight: 800, overflowWrap: "anywhere" }}>{previewUrl || "YOURWEBSITE.COM"}</div>
                    <a
                      href={promotion.promotionUrl}
                      target="_blank"
                      rel="noreferrer"
                      style={{ display: "inline-block", borderRadius: 8, background: "#fbd319", color: "#082d68", padding: "7px 11px", fontSize: 11, fontWeight: 900, textTransform: "uppercase" }}
                    >
                      {promotion.callToAction || "Explore now"} <BannerIcon name="bi-arrow-up-right" className="ms-1" />
                    </a>
                  </div>
                </div>
                <div className="d-flex flex-wrap gap-2 mt-2" style={{ position: "relative", zIndex: 1 }}>
                  {FEATURES.map((feature) => (
                    <span key={`preview-2-${feature.label}`} className="d-inline-flex align-items-center gap-2" style={{ padding: "5px 9px", borderRadius: 999, background: "rgba(255,255,255,.13)", color: "#fff", fontSize: 9, fontWeight: 700, textTransform: "uppercase" }}>
                      <BannerIcon name={feature.icon} style={{ color: "#fbd319" }} />{feature.label}
                    </span>
                  ))}
                </div>
              </div>

              <div style={{ padding: "10px clamp(12px, 2.5vw, 22px)" }}>
                {visibleFareGroups.map((group) => (
                  <div key={`preview-2-${group.id}`} style={{ padding: "6px 0 8px", borderTop: "1px solid #e8edf2" }}>
                    <div className="d-flex align-items-center gap-2 mb-1" style={{ color: group.color, fontSize: 9, fontWeight: 900, letterSpacing: .6, textTransform: "uppercase" }}>
                      <BannerIcon name={group.icon} />
                      {group.title || "Fare"} series
                      <span style={{ marginLeft: "auto", color: "#8a9ab5", fontSize: 8, fontWeight: 700, letterSpacing: 0 }}>{group.fares.length} offers</span>
                    </div>
                    <div className="row g-2">
                      {group.fares.map((fare, fareIndex) => (
                        <div key={`preview-2-${group.id}-${fareIndex}`} className="col-12 col-sm-6 col-xl-3">
                          <div className="d-flex align-items-stretch h-100" style={{ minHeight: 48, border: "1px solid #e2e8f0", borderRadius: 8, overflow: "hidden", background: "#fff", color: "#1e293b" }}>
                            <div className="d-flex flex-column justify-content-center" style={{ flex: 1, minWidth: 0, padding: "5px 7px" }}>
                              <strong className="d-flex align-items-center gap-1" style={{ color: "#1e293b", fontSize: "clamp(9px, 1.05vw, 11px)", lineHeight: 1.2, whiteSpace: "nowrap" }}>
                                <span>{fare.route || "Route"}</span>
                                <BannerIcon name="bi-airplane-fill" style={{ flexShrink: 0, color: group.color, fontSize: 8 }} />
                              </strong>
                              <div className="d-flex align-items-center gap-1 mt-1" style={{ color: "#64748b", fontSize: 7, fontWeight: 700, lineHeight: 1.1, whiteSpace: "nowrap" }}>
                                <span>{fare.airline || "—"}</span>
                                <span aria-hidden="true">·</span>
                                <span>{formatFareDate(fare.date) || "—"}</span>
                              </div>
                            </div>
                            <div className="d-flex flex-column justify-content-center" style={{ minWidth: 64, padding: "4px 5px", borderLeft: "1px dashed #cbd5e1", background: "#f8fafc" }}>
                              <span style={{ color: "#8a9ab5", fontSize: 7, fontWeight: 900, letterSpacing: .5 }}>FARE FROM</span>
                              <strong style={{ color: group.color, fontSize: "clamp(10px, 1.1vw, 12px)", lineHeight: 1.2, overflowWrap: "anywhere" }}>{fare.price || "—"}</strong>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div style={{ marginTop: 5, color: "#64748b", fontSize: 8, fontWeight: 600 }}>
                      Fares and dates for other sectors available
                    </div>
                  </div>
                ))}
              </div>
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-2" style={{ padding: "8px clamp(12px, 2.5vw, 22px)", background: "#e9f1fb", color: "#0b367b" }}>
                <strong style={{ fontSize: 12, overflowWrap: "anywhere" }}>{previewUrl || "YOURWEBSITE.COM"}</strong>
                <span style={{ color: "#475569", fontSize: 10, fontWeight: 600 }}>Your trusted partner for lowest airfares worldwide</span>
                <span className="d-inline-flex align-items-center gap-2" style={{ color: "#0b367b", fontSize: 10, fontWeight: 700 }}>
                  <BannerIcon name="bi-headset" style={{ fontSize: 17 }} />24×7 Agent Support
                </span>
              </div>
            </div>
          ) : selectedPreview === 3 ? (
            <div
              ref={activePreviewRef}
              className="card border-0 shadow-sm"
              style={{ position: "relative", borderRadius: 16, overflow: "hidden", background: backgroundImage ? "#fff" : "#f2f7f6" }}
            >
              {backgroundImage && (
                <>
                  <img
                    src={backgroundImage}
                    alt=""
                    aria-hidden="true"
                    style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", pointerEvents: "none" }}
                  />
                  <div aria-hidden="true" style={{ position: "absolute", inset: 0, background: "rgba(242,247,246,.9)", pointerEvents: "none" }} />
                </>
              )}
              <div style={{ position: "relative", zIndex: 1, padding: "clamp(12px, 2.5vw, 22px)" }}>
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-3" style={{ padding: "12px 15px", borderRadius: "12px 12px 0 0", background: "#123d46", color: "#fff" }}>
                  <div className="d-flex align-items-center gap-2">
                    {logoImage ? (
                      <img src={logoImage} alt={`${promotion.brandName || "Company"} logo`} style={{ display: "block", maxWidth: "min(70vw, 220px)", maxHeight: 48, objectFit: "contain", borderRadius: 5, background: "#fff", padding: 4 }} />
                    ) : (
                      <>
                        <span className="d-inline-flex align-items-center justify-content-center" style={{ width: 36, height: 36, borderRadius: "50%", background: "#f4c95d", color: "#123d46", fontSize: 17 }}>
                          <BannerIcon name="bi-airplane-fill" />
                        </span>
                        <strong style={{ fontSize: 15 }}>{promotion.brandName || "Your Travel Brand"}</strong>
                      </>
                    )}
                  </div>
                  <div className="d-flex align-items-center gap-2" style={{ color: "#d9e8e5", fontSize: 9, fontWeight: 800, letterSpacing: 1, textTransform: "uppercase" }}>
                    <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#f4c95d" }} />
                    Special fares · Ready for takeoff
                  </div>
                </div>

                <div className="d-flex flex-wrap align-items-end justify-content-between gap-3" style={{ padding: "clamp(18px, 3.5vw, 30px) clamp(8px, 1.5vw, 14px)" }}>
                  <div style={{ flex: "1 1 300px" }}>
                    <div style={{ color: "#e2774b", fontSize: 9, fontWeight: 900, letterSpacing: 2, textTransform: "uppercase" }}>Your next great escape</div>
                    <h2 style={{ margin: "7px 0 6px", color: "#123d46", fontSize: "clamp(23px, 4vw, 40px)", lineHeight: 1.03, fontWeight: 900, textTransform: "uppercase" }}>
                      {promotion.headline || "Your promotional offer"}
                    </h2>
                    <div style={{ color: "#587176", fontSize: "clamp(11px, 1.5vw, 14px)", fontWeight: 600 }}>
                      {promotion.subheading || "Add a supporting message"}
                    </div>
                  </div>
                  <a
                    href={promotion.promotionUrl}
                    target="_blank"
                    rel="noreferrer"
                    style={{ display: "inline-flex", alignItems: "center", gap: 8, borderRadius: 6, background: "#e2774b", color: "#fff", padding: "10px 14px", fontSize: 10, fontWeight: 900, letterSpacing: .5, textTransform: "uppercase" }}
                  >
                    {promotion.callToAction || "Explore now"} <BannerIcon name="bi-arrow-up-right" />
                  </a>
                </div>

                <div style={{ borderRadius: 10, overflow: "hidden", background: "#fff", boxShadow: "0 7px 22px rgba(18,61,70,.1)" }}>
                  <div className="d-flex align-items-center justify-content-between gap-2" style={{ padding: "10px 13px", background: "#dceae6", color: "#123d46" }}>
                    <div className="d-flex align-items-center gap-2">
                      <BannerIcon name="bi-signpost-split-fill" style={{ color: "#e2774b" }} />
                      <strong style={{ fontSize: 10, letterSpacing: 1, textTransform: "uppercase" }}>Departure board</strong>
                    </div>
                    <span style={{ color: "#587176", fontSize: 8, fontWeight: 800, letterSpacing: .8 }}>UP TO 4 FARES PER ROW</span>
                  </div>

                  {visibleFareGroups.map((group) => (
                    <div key={`preview-3-${group.id}`} style={{ padding: "10px 12px 12px", borderTop: "1px solid #e9efed" }}>
                      <div className="d-flex align-items-center gap-2 mb-2" style={{ color: group.color, fontSize: 9, fontWeight: 900, letterSpacing: .6, textTransform: "uppercase" }}>
                        <BannerIcon name={group.icon} />
                        {group.title || "Fare"} series
                        <span style={{ marginLeft: "auto", color: "#8a9998", fontSize: 8, fontWeight: 700, letterSpacing: 0 }}>{group.fares.length} offers</span>
                      </div>
                      <div className="row g-2">
                        {group.fares.map((fare, fareIndex) => (
                          <div key={`preview-3-${group.id}-${fareIndex}`} className="col-12 col-sm-6 col-xl-3">
                            <div className="d-flex align-items-stretch h-100" style={{ minHeight: 58, border: "1px solid #e9e3d9", borderRadius: 8, overflow: "hidden", background: "#fffefa", color: "#26464b" }}>
                              <div className="d-flex flex-column justify-content-center" style={{ flex: 1, minWidth: 0, padding: "7px 8px" }}>
                                <strong className="d-flex align-items-center gap-1" style={{ color: "#26464b", fontSize: "clamp(9px, 1.05vw, 11px)", lineHeight: 1.2, whiteSpace: "nowrap" }}>
                                  <span>{fare.route || "Route"}</span>
                                  <BannerIcon name="bi-airplane-fill" style={{ flexShrink: 0, color: "#e2774b", fontSize: 8 }} />
                                </strong>
                                <div className="d-flex align-items-center gap-1 mt-1" style={{ color: "#688083", fontSize: 7, fontWeight: 700, lineHeight: 1.2, whiteSpace: "nowrap" }}>
                                  <span>{fare.airline || "—"}</span>
                                  <span aria-hidden="true">·</span>
                                  <span>{formatFareDate(fare.date) || "—"}</span>
                                </div>
                              </div>
                              <div className="d-flex flex-column justify-content-center" style={{ minWidth: 68, padding: "6px 6px", borderLeft: "1px dashed #d8d4ca", background: "#f8f5ed" }}>
                                <span style={{ color: "#8a9998", fontSize: 7, fontWeight: 900, letterSpacing: .5 }}>FARE FROM</span>
                                <strong style={{ color: group.color, fontSize: "clamp(10px, 1.1vw, 12px)", lineHeight: 1.2, overflowWrap: "anywhere" }}>{fare.price || "—"}</strong>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div style={{ marginTop: 5, color: "#688083", fontSize: 8, fontWeight: 600 }}>
                        Fares and dates for other sectors available
                      </div>
                    </div>
                  ))}
                </div>

                <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mt-3" style={{ padding: "10px 13px", borderRadius: 8, background: "#123d46", color: "#fff" }}>
                  <strong style={{ fontSize: 11, overflowWrap: "anywhere" }}>{previewUrl || "YOURWEBSITE.COM"}</strong>
                  <span className="d-inline-flex align-items-center gap-2" style={{ color: "#d9e8e5", fontSize: 9, fontWeight: 700 }}>
                    <BannerIcon name="bi-headset" style={{ color: "#f4c95d", fontSize: 15 }} />24×7 Agent Support
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <div
              ref={activePreviewRef}
              className="card border-0 shadow-sm"
              style={{ position: "relative", borderRadius: 16, overflow: "hidden", background: backgroundImage ? "#fff" : "#f3efe5" }}
            >
              {backgroundImage && (
                <>
                  <img
                    src={backgroundImage}
                    alt=""
                    aria-hidden="true"
                    style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", pointerEvents: "none" }}
                  />
                  <div aria-hidden="true" style={{ position: "absolute", inset: 0, background: "rgba(243, 239, 229, .88)", pointerEvents: "none" }} />
                </>
              )}
              <div style={{ position: "relative", zIndex: 1, padding: "clamp(12px, 2.5vw, 22px)", color: "#172b3a" }}>
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-3" style={{ padding: "12px 14px", borderRadius: 12, background: "rgba(255,255,255,.9)", boxShadow: "0 4px 16px rgba(23,43,58,.08)" }}>
                  <div className="d-flex align-items-center gap-2">
                    {logoImage ? (
                      <img src={logoImage} alt={`${promotion.brandName || "Company"} logo`} style={{ display: "block", maxWidth: "min(70vw, 220px)", maxHeight: 48, objectFit: "contain", borderRadius: 5, background: "#fff", padding: 4 }} />
                    ) : (
                      <>
                        <span className="d-inline-flex align-items-center justify-content-center" style={{ width: 38, height: 38, borderRadius: 10, color: "#fff", background: "#e45b35", fontSize: 18 }}>
                          <BannerIcon name="bi-airplane-fill" />
                        </span>
                        <strong style={{ color: "#172b3a", fontSize: 16 }}>{promotion.brandName || "Your Travel Brand"}</strong>
                      </>
                    )}
                  </div>
                  <span className="d-inline-flex align-items-center gap-2" style={{ color: "#60717b", fontSize: 9, fontWeight: 800, letterSpacing: 1.3, textTransform: "uppercase" }}>
                    <BannerIcon name="bi-globe-americas" style={{ color: "#e45b35", fontSize: 15 }} />
                    Your next trip starts here
                  </span>
                </div>

                <div className="d-flex flex-wrap align-items-center justify-content-between gap-3" style={{ padding: "clamp(20px, 4vw, 34px) 8px clamp(16px, 3vw, 24px)" }}>
                  <div style={{ flex: "1 1 300px" }}>
                    <div style={{ color: "#e45b35", fontSize: 10, fontWeight: 900, letterSpacing: 2.5, textTransform: "uppercase" }}>Your ticket to better fares</div>
                    <h2 style={{ margin: "7px 0 5px", color: "#172b3a", fontSize: "clamp(25px, 4.4vw, 45px)", lineHeight: 1, fontWeight: 950, textTransform: "uppercase" }}>
                      {promotion.headline || "Your promotional offer"}
                    </h2>
                    <p style={{ margin: 0, color: "#60717b", fontSize: "clamp(12px, 1.6vw, 15px)", fontWeight: 600 }}>
                      {promotion.subheading || "Add a supporting message"}
                    </p>
                  </div>
                  <a
                    href={promotion.promotionUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="d-inline-flex align-items-center gap-2"
                    style={{ padding: "11px 15px", borderRadius: 6, background: "#e45b35", color: "#fff", fontSize: 11, fontWeight: 900, letterSpacing: .5, textTransform: "uppercase", boxShadow: "0 5px 12px rgba(228,91,53,.2)" }}
                  >
                    {promotion.callToAction || "Explore now"} <BannerIcon name="bi-arrow-up-right" />
                  </a>
                </div>

                <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
                  <strong style={{ color: "#172b3a", fontSize: 11, letterSpacing: 1.2, textTransform: "uppercase" }}>Featured departures</strong>
                  <span style={{ color: "#8b9698", fontSize: 9, fontWeight: 700 }}>SERIES FARE COLLECTION</span>
                </div>
                <div className="row g-2">
                  {visibleFareGroups.map((group) => (
                    <div className="col-12 col-md-4" key={`preview-4-${group.id}`}>
                      <div style={{ height: "100%", borderRadius: 10, background: "rgba(255,255,255,.96)", boxShadow: "0 5px 18px rgba(23,43,58,.08)", overflow: "hidden" }}>
                        <div className="d-flex align-items-center justify-content-between gap-2" style={{ padding: "9px 11px", borderTop: `3px solid ${group.color}` }}>
                          <strong style={{ color: "#172b3a", fontSize: 10, letterSpacing: .8, textTransform: "uppercase" }}>{group.title || "Fare"} routes</strong>
                          <BannerIcon name={group.icon} style={{ color: group.color }} />
                        </div>
                        {group.fares.map((fare, fareIndex) => (
                          <div key={`preview-4-${group.id}-${fareIndex}`} className="d-flex align-items-stretch" style={{ margin: "0 8px 8px", border: "1px solid #e8e5dd", borderRadius: 8, overflow: "hidden", background: "#fffefa" }}>
                            <div style={{ flex: 1, minWidth: 0, padding: "9px 8px" }}>
                              <div className="d-flex align-items-center gap-1">
                                <strong style={{ color: "#172b3a", fontSize: "clamp(10px, 1.3vw, 12px)" }}>{fare.route || "Route"}</strong>
                                <BannerIcon name="bi-airplane-fill" style={{ color: "#e45b35", fontSize: 9 }} />
                              </div>
                              <div className="d-flex align-items-center gap-2 mt-1" style={{ color: "#829094", fontSize: 8, fontWeight: 700, textTransform: "uppercase" }}>
                                <span>{fare.airline || "Airline"}</span>
                                <span>·</span>
                                <span>{formatFareDate(fare.date) || "Date"}</span>
                              </div>
                            </div>
                            <div className="d-flex flex-column align-items-center justify-content-center" style={{ minWidth: 74, padding: "6px 8px", borderLeft: "1px dashed #d8d4ca", background: "#f8f5ed" }}>
                              <span style={{ color: "#9aa3a3", fontSize: 7, fontWeight: 800, letterSpacing: .8 }}>FARE FROM</span>
                              <strong style={{ marginTop: 2, color: group.color, fontSize: "clamp(11px, 1.4vw, 14px)", whiteSpace: "nowrap" }}>{fare.price || "—"}</strong>
                            </div>
                          </div>
                        ))}
                        <div style={{ padding: "0 8px 8px", color: "#829094", fontSize: 8, fontWeight: 600 }}>
                          Fares and dates for other sectors available
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mt-3" style={{ padding: "10px 12px", borderRadius: 8, background: "#172b3a", color: "#fff" }}>
                  <strong style={{ fontSize: 11, overflowWrap: "anywhere" }}>{previewUrl || "YOURWEBSITE.COM"}</strong>
                  <span className="d-flex align-items-center gap-2" style={{ color: "#f6d7b9", fontSize: 9, fontWeight: 700 }}>
                    <BannerIcon name="bi-headset" style={{ color: "#f2a47d", fontSize: 15 }} />24×7 Agent Support
                  </span>
                </div>
              </div>
            </div>
          )}
          {(promotion.startDate || promotion.endDate) && (
            <div className="mt-2 text-end" style={{ color: "#64748b", fontSize: 12 }}>
              Campaign dates: {promotion.startDate || "—"} to {promotion.endDate || "—"}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Promotions;
